// ** React
import React from "react";

// ** Components
import Text from "components/typography/Text";
import OnlineCircle from "components/user/OnlineCircle";

/**
 * Funkcja pomocnicza do renderowania statusu online z kółkiem
 */
const renderStatusWithCircle = ({
	online,
	status,
	userLogin,
	textColor,
	statusText,
	noOnlineCircle, // A/B Tests
}) => {
	return (
		<>
			{!noOnlineCircle && (
				<Text
					style={{
						marginRight: "4px",
						display: "inline-block",
						verticalAlign: "text-bottom",
						height: "12px",
					}}
				>
					<OnlineCircle
						online={online}
						status={status}
						userLogin={userLogin}
						size="small"
						noBorderAndOutline
					/>
				</Text>
			)}
			<Text small color={noOnlineCircle ? "grey" : textColor}>
				{statusText}
			</Text>
		</>
	);
};

/**
 * Funkcja pomocnicza do renderowania ostatniej aktywności
 */
const renderLastActivity = ({
	t,
	formattedLastActivityDate,
	customGrayColor,
}) => (
	<Text
		small
		color={customGrayColor ? undefined : "grey"}
		style={{ color: customGrayColor }}
	>
		{t("common:lastActivity")}: {formattedLastActivityDate}
	</Text>
);

/**
 * Główna funkcja do renderowania statusu użytkownika
 * @param {Object} params - Parametry funkcji
 * @param {number} params.status - Status profilu użytkownika (1-4)
 * @param {number} params.online - Status online (0-2)
 * @param {boolean} params.isMe - Czy to aktualnie zalogowany użytkownik
 * @param {string} params.userLogin - Login użytkownika
 * @param {function} params.t - Funkcja tłumaczenia
 * @param {string} params.formattedLastActivityDate - Sformatowana data ostatniej aktywności
 * @param {string} params.customGrayColor - Kolor tekstu szarego
 * @returns {JSX.Element} - Element JSX z odpowiednim statusem
 */
export const getUserStatus = ({
	status,
	online,
	isMe,
	userLogin,
	t,
	formattedLastActivityDate,
	customGrayColor,
	noOnlineCircle, // A/B Tests
}) => {
	// Logika dla zalogowanego użytkownika
	if (isMe) {
		switch (status) {
			case 1:
				return online === 1
					? renderStatusWithCircle({
							online,
							status,
							userLogin,
							textColor: "onlineYellow",
							statusText: t("common:status:onlineYellow"),
							noOnlineCircle,
						})
					: renderStatusWithCircle({
							online,
							status,
							userLogin,
							textColor: "online",
							statusText: t("common:status:online"),
							noOnlineCircle,
						});

			case 2:
				return renderStatusWithCircle({
					online,
					status,
					userLogin,
					textColor: "meet",
					statusText: t("common:status:meet"),
					noOnlineCircle,
				});

			case 3:
				return renderStatusWithCircle({
					online,
					status,
					userLogin,
					textColor: "notDisturb",
					statusText: t("common:status:invisiable"),
					noOnlineCircle,
				});

			case 4:
			default:
				return renderLastActivity({
					t,
					formattedLastActivityDate,
					customGrayColor,
					noOnlineCircle,
				});
		}
	}

	// Logika dla innych użytkowników - jeśli status 1 lub online 0
	if (status === 1 || online === 0) {
		switch (online) {
			case 1:
				return renderStatusWithCircle({
					online,
					status,
					userLogin,
					textColor: "onlineYellow",
					statusText: t("common:status:onlineYellow"),
					customGrayColor,
					noOnlineCircle,
				});

			case 2:
				return renderStatusWithCircle({
					online,
					status,
					userLogin,
					textColor: "online",
					statusText: t("common:status:online"),
					customGrayColor,
					noOnlineCircle,
				});

			default:
				return renderLastActivity({
					t,
					formattedLastActivityDate,
					customGrayColor,
					noOnlineCircle,
				});
		}
	}

	// Logika dla innych użytkowników - pozostałe przypadki
	switch (status) {
		case 2:
			return renderStatusWithCircle({
				online,
				status,
				userLogin,
				textColor: "meet",
				statusText: t("common:status:meet"),
				customGrayColor,
				noOnlineCircle,
			});

		case 3:
			return renderStatusWithCircle({
				online,
				status,
				userLogin,
				textColor: "notDisturb",
				statusText: t("common:status:invisiable"),
				customGrayColor,
				noOnlineCircle,
			});

		case 4:
		default:
			return renderLastActivity({
				t,
				formattedLastActivityDate,
				customGrayColor,
				noOnlineCircle,
			});
	}
};

export default getUserStatus;
